// services/api/client.js
// ----------------------------------------------------------------------------
// Instance axios partagée + intercepteurs (auth header, silent refresh, erreurs).
// Extrait du gros api.js pour faciliter la maintenance. La logique métier des
// 20 domaines reste dans services/api.js (à splitter plus tard, refacto plus
// large à faire post-launch).
import axios from 'axios';

// === Configuration Axios ===
export const apiClient = axios.create({
  baseURL: '/api',
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  }
});

// === Intercepteur pour token d'auth ===
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// === Silent refresh state ===
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

// === Intercepteur pour erreurs avec silent refresh ===
apiClient.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const originalRequest = error.config;

    // 401 → silent refresh (sauf sur /auth/refresh-token et /auth/login eux-mêmes)
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (originalRequest.url === '/auth/refresh-token' || originalRequest.url === '/auth/login') {
        localStorage.removeItem('token');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.post('/api/auth/refresh-token', {}, { withCredentials: true });
        const newToken = response.data.token;

        localStorage.setItem('token', newToken);
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        processQueue(null, newToken);

        // Notifier AuthContext du nouveau token
        window.dispatchEvent(new CustomEvent('tokenRefreshed', { detail: { token: newToken } }));

        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (error.response?.status === 429) {
      const e = new Error(error.response.data.message || 'Quota dépassé');
      e.code = error.response.data?.code || 'QUOTA_EXCEEDED';
      e.status = 429;
      e.response = error.response; // préserver pour les composants qui lisent err.response
      throw e;
    }

    const e = new Error(
      error.response?.data?.message ||
      error.message ||
      'Une erreur est survenue'
    );
    if (error.response?.data?.code) e.code = error.response.data.code;
    if (error.response?.status) e.status = error.response.status;
    // Préserver error.response : ~20 composants lisent err.response?.status / err.response?.data
    // (paywall Siège Gardien, détection 409 invitation, messages backend...).
    e.response = error.response;
    throw e;
  }
);

// Re-export axios pour les usages directs (downloads blob qui contournent
// l'intercepteur de transformation response.data).
export { axios };
